import React, { useState } from 'react';
import { Phone, MapPin, Globe, Mail, Tag, Star, ChevronDown, ChevronUp, Navigation } from 'lucide-react';
import HygieneRatingBadge from '../CanvassingData/HygieneRatingBadge';
import CallScript from './CallScript';
import NotesSection from './NotesSection';

const NewLeadView = ({ lead, notes, userProfile, onCall, onStatusChange, onCallback, onConvert, disabled }) => {
  const [showAllEmails, setShowAllEmails] = useState(false);

  if (!lead) {
    return (
      <div className="text-center py-16 text-gray-500">
        <p className="text-lg font-medium">No new leads available</p>
        <p className="text-sm mt-1">Check Recent Leads or Callbacks</p>
      </div>
    );
  }

  const address = [lead.address_line_1, lead.address_town, lead.address_postcode].filter(Boolean).join(', ');

  // Parse emails - could be comma-separated
  const emails = lead.email ? lead.email.split(',').map(e => e.trim()).filter(Boolean) : [];
  const visibleEmails = showAllEmails ? emails : emails.slice(0, 1);
  const hiddenCount = emails.length - 1;

  // Google rating stars
  const renderStars = (rating) => {
    const full = Math.round(rating);
    return '★'.repeat(full) + '☆'.repeat(5 - full);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto p-3 pb-48 space-y-3">
        {/* Top: Business name + category + hygiene */}
        <div className="bg-white rounded-xl shadow-sm border p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-gray-900 text-lg leading-tight">{lead.business_name}</h2>
              {lead.category_name && (
                <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                  <Tag size={10} />
                  <span>{lead.category_name}</span>
                </div>
              )}
            </div>
            {lead.fhrs_id && (
              <div className="flex-shrink-0">
                <HygieneRatingBadge lead={lead} />
              </div>
            )}
          </div>

          {/* Address */}
          {address && (
            <div className="flex items-start gap-1.5 text-sm text-gray-600 mt-2">
              <MapPin size={14} className="mt-0.5 flex-shrink-0" />
              <div>
                <span>{address}</span>
                {lead.distance != null && (
                  <div className="flex items-center gap-1 text-xs text-blue-600 font-medium mt-0.5">
                    <Navigation size={10} />
                    <span>{(lead.distance * 0.621371).toFixed(1)} miles away ({Math.max(5, Math.round(((lead.distance * 0.621371) / 30 * 60 + 5) / 5) * 5)} mins)</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Google rating */}
          {lead.rating && (
            <div className="text-sm text-gray-600 mt-1.5 ml-5">
              <span className="text-amber-500">{renderStars(lead.rating)}</span>
              <span className="ml-1">({lead.rating})</span>
              {lead.review_count > 0 && <span> · {lead.review_count} reviews</span>}
            </div>
          )}

          {/* Website */}
          {lead.website && (
            <div className="mt-2 ml-5">
              <a
                href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600"
              >
                <Globe size={13} />
                <span className="truncate">{lead.website.replace(/^https?:\/\//, '')}</span>
              </a>
            </div>
          )}

          {/* Email(s) */}
          {emails.length > 0 && (
            <div className="mt-1.5 ml-5">
              {visibleEmails.map((email, i) => (
                <a
                  key={i}
                  href={`mailto:${email}`}
                  className="flex items-center gap-1 text-sm text-blue-600"
                >
                  <Mail size={13} />
                  <span className="truncate">{email}</span>
                </a>
              ))}
              {!showAllEmails && hiddenCount > 0 && (
                <button
                  onClick={() => setShowAllEmails(true)}
                  className="text-xs text-blue-500 font-medium ml-4 mt-0.5"
                >
                  +{hiddenCount} more
                </button>
              )}
              {showAllEmails && hiddenCount > 0 && (
                <button
                  onClick={() => setShowAllEmails(false)}
                  className="text-xs text-blue-500 font-medium ml-4 mt-0.5"
                >
                  Show less
                </button>
              )}
            </div>
          )}
        </div>

        {/* CALL button — big and prominent */}
        {lead.phone_number ? (
          <a
            href={`tel:${lead.phone_number}`}
            onClick={() => onCall(lead)}
            className="flex items-center justify-center gap-3 min-h-[56px] rounded-xl bg-green-600 active:bg-green-800 text-white font-bold text-lg shadow-sm transition-all"
          >
            <Phone size={22} />
            <span>{lead.phone_number}</span>
          </a>
        ) : (
          <div className="flex items-center justify-center gap-2 min-h-[56px] rounded-xl bg-gray-200 text-gray-500 font-medium">
            <Phone size={20} />
            <span>No Phone Number</span>
          </div>
        )}

        {/* Call Script */}
        <CallScript lead={lead} userName={userProfile?.name} />

        {/* Notes Section */}
        <div className="bg-white rounded-xl shadow-sm border p-3">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Notes</h3>
          <NotesSection leadId={lead.id} initialNotes={notes || []} />
        </div>

        {/* Location timestamp — hidden until scroll */}
        <LocationTimestamp />
      </div>

      {/* Fixed status buttons at bottom — offset for sidebar on desktop */}
      <div
        className="fixed bottom-0 left-0 right-0 md:left-64 bg-white border-t shadow-lg p-3 z-50"
        style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
      >
        <StickyStatusButtons
          lead={lead}
          onStatusChange={onStatusChange}
          onCallback={onCallback}
          onConvert={onConvert}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

/**
 * Status buttons rendered sticky at the bottom of the screen
 */
const StickyStatusButtons = ({ lead, onStatusChange, onCallback, onConvert, disabled }) => {
  if (lead.canvassing_status === 'converted') {
    return (
      <div className="p-2 bg-purple-50 text-center rounded-lg">
        <span className="text-purple-700 font-semibold">Already Converted</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-4 gap-1.5">
        <button
          onClick={() => onStatusChange(lead.id, 'interested', 'interested')}
          disabled={disabled || lead.canvassing_status === 'interested'}
          className={`flex flex-col items-center justify-center min-h-[52px] rounded-xl text-white font-semibold text-xs
            bg-green-500 active:bg-green-700
            ${lead.canvassing_status === 'interested' ? 'opacity-50 ring-2 ring-white' : ''}
            ${disabled ? 'opacity-50' : ''}`}
        >
          <span>Book</span>
        </button>
        <button
          onClick={() => onStatusChange(lead.id, 'not_interested', 'not_interested')}
          disabled={disabled || lead.canvassing_status === 'not_interested'}
          className={`flex flex-col items-center justify-center min-h-[52px] rounded-xl text-white font-semibold text-xs
            bg-red-500 active:bg-red-700
            ${lead.canvassing_status === 'not_interested' ? 'opacity-50 ring-2 ring-white' : ''}
            ${disabled ? 'opacity-50' : ''}`}
        >
          <span>Not</span>
          <span>Interested</span>
        </button>
        <button
          onClick={() => onCallback(lead)}
          disabled={disabled || lead.canvassing_status === 'callback'}
          className={`flex flex-col items-center justify-center min-h-[52px] rounded-xl text-white font-semibold text-xs
            bg-amber-500 active:bg-amber-700
            ${lead.canvassing_status === 'callback' ? 'opacity-50 ring-2 ring-white' : ''}
            ${disabled ? 'opacity-50' : ''}`}
        >
          <span>Callback</span>
        </button>
        <button
          onClick={() => onStatusChange(lead.id, 'do_not_contact', null)}
          disabled={disabled || lead.canvassing_status === 'do_not_contact'}
          className={`flex flex-col items-center justify-center min-h-[52px] rounded-xl text-white font-semibold text-xs
            bg-gray-600 active:bg-gray-800
            ${lead.canvassing_status === 'do_not_contact' ? 'opacity-50 ring-2 ring-white' : ''}
            ${disabled ? 'opacity-50' : ''}`}
        >
          <span>Do Not</span>
          <span>Call</span>
        </button>
      </div>
      {(lead.canvassing_status === 'interested' || lead.canvassing_status === 'callback' || lead.canvassing_status === 'contacted') && (
        <button
          onClick={() => onConvert(lead)}
          disabled={disabled}
          className="w-full flex items-center justify-center gap-2 min-h-[48px] rounded-xl bg-blue-600 active:bg-blue-800 text-white font-bold text-base transition-all"
        >
          Book Survey
        </button>
      )}
    </div>
  );
};

const LocationTimestamp = () => {
  try {
    const saved = JSON.parse(localStorage.getItem('canvassing_last_position'));
    if (saved?.ts) {
      const d = new Date(saved.ts);
      const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      return (
        <p className="text-center text-xs text-gray-400 py-2">
          Location updated {date} at {time}
        </p>
      );
    }
  } catch {}
  return null;
};

export default NewLeadView;
